import { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import {
  SendHorizonal, Plus, Search, CheckCircle2, Clock, AlertTriangle,
  ChevronRight, X, Flag, Trash2, User
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';

const DEMO_HANDOFFS = [
  { id: 'HN-001', patient: 'John Smith', provider: 'Dr. Sarah Chen', date: '2026-03-25', type: 'Post-visit', status: 'sent', urgency: 'normal', summary: 'Diabetes follow-up completed. HbA1c improved to 6.8%. Continue current Metformin regimen.', tasks: ['Schedule 3-month follow-up', 'Order HbA1c recheck'], flagNote: '' },
  { id: 'HN-002', patient: 'Maria Garcia', provider: 'Dr. James Wilson', date: '2026-03-25', type: 'Urgent Flag', status: 'pending', urgency: 'urgent', summary: 'New patient presented with chest discomfort. EKG normal but troponin pending.', tasks: ['Monitor troponin results', 'Cardiology consult if elevated', 'Call patient with results'], flagNote: 'Troponin results expected by 3PM — please follow up urgently.' },
  { id: 'HN-003', patient: 'Robert Johnson', provider: 'Dr. Sarah Chen', date: '2026-03-25', type: 'Referral', status: 'acknowledged', urgency: 'normal', summary: 'Referred to pulmonology for persistent cough. Chest X-ray clear.', tasks: ['Fax referral to Dr. Patel', 'Schedule follow-up in 2 weeks'], flagNote: '' },
  { id: 'HN-004', patient: 'Emily Davis', provider: 'Dr. Maria Santos', date: '2026-03-24', type: 'Post-visit', status: 'draft', urgency: 'low', summary: 'Annual cardiology follow-up. Echo stable. No medication changes.', tasks: ['Schedule next annual', 'Send summary to PCP'], flagNote: '' },
];

const PROVIDERS = ['All', 'Dr. Sarah Chen', 'Dr. James Wilson', 'Dr. Maria Santos'];
const TEMPLATES = [
  { label: 'Post-visit', summary: 'Visit completed. No acute concerns.', tasks: ['Schedule follow-up', 'Send visit summary'] },
  { label: 'Referral', summary: 'Patient referred to specialist.', tasks: ['Fax referral', 'Schedule follow-up', 'Confirm appointment'] },
  { label: 'Urgent Flag', summary: 'Urgent attention required.', tasks: ['Follow up on pending results', 'Contact patient'] },
];

const HandoffNotesPage = () => {
  const { isDemo } = useAppMode();
  const [handoffs, setHandoffs] = useState(DEMO_HANDOFFS);
  const [filter, setFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  // Form state
  const [formPatient, setFormPatient] = useState('');
  const [formProvider, setFormProvider] = useState('Dr. Sarah Chen');
  const [formSummary, setFormSummary] = useState('');
  const [formTasks, setFormTasks] = useState(['']);
  const [formFlag, setFormFlag] = useState(false);
  const [formFlagNote, setFormFlagNote] = useState('');
  const [formUrgency, setFormUrgency] = useState('normal');

  const filtered = handoffs.filter(h => {
    const matchesFilter = filter === 'all' || h.status === filter;
    const matchesProvider = providerFilter === 'All' || h.provider === providerFilter;
    const matchesSearch = !search || h.patient.toLowerCase().includes(search.toLowerCase()) || h.summary.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesProvider && matchesSearch;
  });

  const startCreate = (template) => {
    setFormPatient('');
    setFormProvider('Dr. Sarah Chen');
    setFormSummary(template?.summary || '');
    setFormTasks(template?.tasks || ['']);
    setFormFlag(false); setFormFlagNote(''); setFormUrgency('normal');
    setEditing('new');
  };

  const startEdit = (h) => {
    setFormPatient(h.patient);
    setFormProvider(h.provider);
    setFormSummary(h.summary);
    setFormTasks([...h.tasks]);
    setFormFlag(!!h.flagNote);
    setFormFlagNote(h.flagNote || '');
    setFormUrgency(h.urgency);
    setEditing(h.id);
  };

  const saveHandoff = () => {
    if (editing === 'new') {
      const newH = {
        id: `HN-${Date.now().toString().slice(-4)}`, patient: formPatient || 'Unknown Patient',
        provider: formProvider, date: new Date().toISOString().split('T')[0], type: formFlag ? 'Urgent Flag' : 'Post-visit',
        status: 'draft', urgency: formUrgency, summary: formSummary, tasks: formTasks.filter(Boolean), flagNote: formFlag ? formFlagNote : '',
      };
      setHandoffs(prev => [newH, ...prev]);
    } else {
      setHandoffs(prev => prev.map(h => h.id === editing ? { ...h, patient: formPatient, provider: formProvider, summary: formSummary, tasks: formTasks.filter(Boolean), urgency: formUrgency, flagNote: formFlag ? formFlagNote : '' } : h));
    }
    setEditing(null);
    toast.success(editing === 'new' ? 'Handoff note created' : 'Handoff note updated');
  };

  const updateStatus = (id, newStatus) => {
    setHandoffs(prev => prev.map(h => h.id === id ? { ...h, status: newStatus } : h));
    toast.success(`Status updated to ${newStatus}`);
  };

  const addTask = () => setFormTasks(prev => [...prev, '']);
  const removeTask = (i) => setFormTasks(prev => prev.filter((_, idx) => idx !== i));
  const updateTask = (i, val) => setFormTasks(prev => prev.map((t, idx) => idx === i ? val : t));

  const statusColor = { draft: 'bg-slate-100 text-slate-600', pending: 'bg-amber-50 text-amber-700', sent: 'bg-blue-50 text-blue-700', acknowledged: 'bg-emerald-50 text-emerald-700' };
  const urgencyColor = { low: 'text-slate-400', normal: 'text-slate-600', urgent: 'text-red-600' };

  if (editing) {
    return (
      <div className="space-y-5" data-testid="handoff-form">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">{editing === 'new' ? 'New Handoff Note' : 'Edit Handoff Note'}</h1>
            <p className="text-sm text-slate-500 mt-0.5">Auto-saves as draft</p>
          </div>
          <Button variant="outline" onClick={() => setEditing(null)} className="h-9 text-xs gap-1"><X className="h-3 w-3" /> Cancel</Button>
        </div>

        {/* Quick templates */}
        {editing === 'new' && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-400 font-medium">Templates:</span>
            {TEMPLATES.map(t => (
              <button key={t.label} onClick={() => { setFormSummary(t.summary); setFormTasks([...t.tasks]); setFormFlag(t.label === 'Urgent Flag'); }}
                className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium transition-all"
                data-testid={`template-${t.label.toLowerCase().replace(/\s/g, '-')}`}>
                {t.label}
              </button>
            ))}
          </div>
        )}

        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-slate-500 mb-1 block font-medium">Patient</label>
                <Input value={formPatient} onChange={e => setFormPatient(e.target.value)} placeholder="Search patient..." className="h-9 text-sm border-slate-200" data-testid="handoff-patient" />
              </div>
              <div>
                <label className="text-[11px] text-slate-500 mb-1 block font-medium">Provider</label>
                <select value={formProvider} onChange={e => setFormProvider(e.target.value)} className="w-full h-9 text-sm border border-slate-200 rounded-md px-3" data-testid="handoff-provider">
                  {PROVIDERS.slice(1).map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-[11px] text-slate-500 mb-1 block font-medium">Visit Summary</label>
              <textarea value={formSummary} onChange={e => setFormSummary(e.target.value)} rows={3} placeholder="Brief visit summary..."
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-slate-400"
                data-testid="handoff-summary" />
            </div>

            <div>
              <label className="text-[11px] text-slate-500 mb-1 block font-medium">Follow-up Tasks</label>
              <div className="space-y-1.5">
                {formTasks.map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={t} onChange={e => updateTask(i, e.target.value)} placeholder="Task..." className="h-8 text-xs flex-1 border-slate-200" data-testid={`handoff-task-${i}`} />
                    {formTasks.length > 1 && <button onClick={() => removeTask(i)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>}
                  </div>
                ))}
                <button onClick={addTask} className="text-[11px] text-slate-500 hover:text-slate-700 font-medium flex items-center gap-1"><Plus className="h-3 w-3" /> Add task</button>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div>
                <label className="text-[11px] text-slate-500 mb-1 block font-medium">Urgency</label>
                <div className="flex gap-1">
                  {['low', 'normal', 'urgent'].map(u => (
                    <button key={u} onClick={() => setFormUrgency(u)}
                      className={cn("text-[11px] px-3 py-1.5 rounded-lg border font-medium capitalize transition-all",
                        formUrgency === u ? (u === 'urgent' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-slate-900 text-white border-slate-900') : 'border-slate-200 text-slate-500')}
                      data-testid={`urgency-${u}`}>
                      {u}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1">
                <label className="text-[11px] text-slate-500 mb-1 block font-medium">Flag for Provider</label>
                <div className="flex items-center gap-2">
                  <button onClick={() => setFormFlag(!formFlag)}
                    className={cn("h-5 w-5 rounded border-2 flex items-center justify-center transition-all",
                      formFlag ? "bg-amber-500 border-amber-500" : "border-slate-300")}
                    data-testid="handoff-flag-toggle">
                    {formFlag && <CheckCircle2 className="h-3 w-3 text-white" />}
                  </button>
                  {formFlag && <Input value={formFlagNote} onChange={e => setFormFlagNote(e.target.value)} placeholder="Note for provider..." className="h-8 text-xs flex-1 border-slate-200" />}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setEditing(null)} className="h-9 text-xs">Discard</Button>
          <Button onClick={saveHandoff} className="h-9 text-xs bg-slate-900 hover:bg-slate-800 gap-1" data-testid="handoff-save">
            <SendHorizonal className="h-3 w-3" /> {editing === 'new' ? 'Save as Draft' : 'Save Changes'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="handoff-notes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Handoff Notes</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage visit handoffs between MA and providers</p>
        </div>
        <Button onClick={() => startCreate()} className="h-9 text-xs bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="create-handoff-btn">
          <Plus className="h-3.5 w-3.5" /> New Handoff
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="handoff-status-filter">
          {[{ key: 'all', label: 'All' }, { key: 'pending', label: 'Pending' }, { key: 'sent', label: 'Sent' }, { key: 'acknowledged', label: 'Acknowledged' }].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={cn("px-3 py-1.5 text-[11px] font-medium rounded-md transition-all",
                filter === f.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}
              data-testid={`filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>
        <select value={providerFilter} onChange={e => setProviderFilter(e.target.value)} className="h-8 text-xs border border-slate-200 rounded-md px-2" data-testid="provider-filter">
          {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <div className="flex-1" />
        <div className="relative w-48">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="h-8 text-xs pl-8 border-slate-200" data-testid="handoff-search" />
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {filtered.map(h => (
          <div key={h.id} className="group p-4 rounded-xl border border-slate-200 hover:bg-slate-50/50 transition-all cursor-pointer"
            onClick={() => startEdit(h)} data-testid={`handoff-${h.id}`}>
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                <User className="h-4 w-4 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-sm font-semibold text-slate-900">{h.patient}</p>
                  <Badge className={cn("text-[9px] border-0", statusColor[h.status])}>{h.status}</Badge>
                  {h.urgency === 'urgent' && <Badge className="text-[9px] bg-red-50 text-red-600 border-0">Urgent</Badge>}
                  {h.flagNote && <Flag className="h-3 w-3 text-amber-500" />}
                </div>
                <p className="text-xs text-slate-500">{h.provider} · {h.date} · {h.type}</p>
                <p className="text-xs text-slate-600 mt-1 line-clamp-1">{h.summary}</p>
                {h.tasks.length > 0 && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <Clock className="h-3 w-3 text-slate-400" />
                    <span className="text-[10px] text-slate-400">{h.tasks.length} task{h.tasks.length > 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                {h.status === 'draft' && (
                  <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={e => { e.stopPropagation(); updateStatus(h.id, 'sent'); }} data-testid={`send-${h.id}`}>
                    Send
                  </Button>
                )}
                {h.status === 'sent' && (
                  <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={e => { e.stopPropagation(); updateStatus(h.id, 'acknowledged'); }} data-testid={`ack-${h.id}`}>
                    Acknowledge
                  </Button>
                )}
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <SendHorizonal className="h-8 w-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No handoff notes match your filters</p>
            <Button onClick={() => startCreate()} variant="outline" className="mt-3 h-8 text-xs gap-1"><Plus className="h-3 w-3" /> Create one</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HandoffNotesPage;
